import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EMAIL_SUPPORT, PHONE_PRO, isQAMode } from '@/lib/tp13Config';

const DELAY_MS = 60000; // 60 secondes
const KEY = 'tp13_lead_modal_last';
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 heures

const EXCLUDED_PATHS = [
  '/login',
  '/signup',
  '/reset',
  '/merci',
  '/admin',
  '/dashboard',
  '/mentions-legales',
  '/cgu',
  '/politique-confidentialite',
  '/cookies',
];

export default function LeadModal() {
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const isCommerceContext = location.pathname.startsWith('/collaborateurs');
  const isAOContext = location.pathname.startsWith('/apporteurs-affaires');

  const ctaCards = useMemo(() => {
    const cards = [
      {
        key: 'commerce',
        to: '/signup?role=COMMERCE',
        title: 'Je suis restaurateur / commerçant',
        subtitle: 'Je crée mon espace et je dépose ma candidature sans engagement.',
        icon: '🍔',
      },
      {
        key: 'ao',
        to: '/signup?role=AO',
        title: "Je suis apporteur d'affaires",
        subtitle: "Je prends de l'avance et j'entre parmi les premiers du réseau.",
        icon: '💼',
      },
    ];

    if (isAOContext) return [cards[1], cards[0]];
    if (isCommerceContext) return [cards[0], cards[1]];
    return cards;
  }, [isAOContext, isCommerceContext]);

  useEffect(() => {
    // Mode QA : ne rien afficher
    if (isQAMode()) {
      return;
    }

    // Vérifier si on est sur une page exclue
    const isExcluded = EXCLUDED_PATHS.some((path) => location.pathname.startsWith(path));
    if (isExcluded) return;

    // Vérifier le cooldown
    const lastShown = localStorage.getItem(KEY);
    if (lastShown) {
      const elapsed = Date.now() - parseInt(lastShown, 10);
      if (elapsed < COOLDOWN_MS) return;
    }

    // Timer 60s
    const timer = setTimeout(() => {
      setShowModal(true);
      localStorage.setItem(KEY, Date.now().toString());
      
      // Tracking soft
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'lead_modal_view',
          category: 'Lead',
          action: 'View',
          label: 'Candidature TP13',
        });
      }
    }, DELAY_MS);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleClose = () => {
    setShowModal(false);
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_modal_close',
        category: 'Lead',
        action: 'Close',
        label: 'Plus tard',
      });
    }
  };

  const handleCTAClick = (type: 'commercant' | 'apporteur') => {
    setShowModal(false);
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_modal_click',
        category: 'Lead',
        action: 'Click',
        label: type === 'commercant' ? 'Commerçant' : 'Apporteur',
      });
    }
  };

  // Mode QA : ne rien afficher
  if (isQAMode()) {
    return null;
  }

  if (!showModal) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 z-[9998]"
        onClick={handleClose}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-tp13-surface border border-tp13-border rounded-xl shadow-2xl max-w-lg w-full">
          <div className="p-6 border-b border-tp13-border">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">🚀 Candidature TP13</h2>
              <button
                onClick={handleClose}
                className="text-tp13-muted hover:text-tp13-text transition-colors cursor-pointer"
                aria-label="Fermer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-tp13-text font-medium">
              Vous pouvez prendre votre place maintenant, sans engagement, pendant que le réseau TP13 se structure.
            </p>
            <p className="text-sm text-tp13-muted leading-relaxed">
              La candidature est gratuite, étudiée humainement, et vous permet d’arriver tôt dans un réseau en construction, sans pression commerciale inutile.
            </p>

            <div className="flex flex-wrap gap-2 text-xs text-tp13-muted">
              <span className="px-3 py-1.5 rounded-full border border-tp13-border bg-tp13-black/40">Gratuit</span>
              <span className="px-3 py-1.5 rounded-full border border-tp13-border bg-tp13-black/40">Sans engagement</span>
              <span className="px-3 py-1.5 rounded-full border border-tp13-border bg-tp13-black/40">Validation humaine</span>
              <span className="px-3 py-1.5 rounded-full border border-tp13-border bg-tp13-black/40">Réponse rapide</span>
            </div>

            <div className="space-y-3">
              {ctaCards.map((card, index) => (
                <Link
                  key={card.key}
                  to={card.to}
                  onClick={() => handleCTAClick(card.key === 'commerce' ? 'commercant' : 'apporteur')}
                  className={`block w-full rounded-lg border px-5 py-4 text-left transition-colors cursor-pointer ${
                    index === 0
                      ? 'bg-tp13-red border-tp13-red text-white hover:bg-tp13-red2'
                      : 'border-tp13-red text-tp13-red hover:bg-tp13-red hover:text-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl leading-none">{card.icon}</span>
                    <div>
                      <div className="font-semibold">{card.title}</div>
                      <div className={`mt-1 text-sm ${index === 0 ? 'text-white/85' : 'text-current/80'}`}>
                        {card.subtitle}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="rounded-lg border border-tp13-border bg-tp13-black/40 p-4 text-sm text-tp13-muted">
              <p>
                Rejoindre maintenant, c’est simplement prendre de l’avance. Si votre profil est validé, vous ferez partie des premiers du réseau sur votre zone.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                <a href={`mailto:${EMAIL_SUPPORT}`} className="hover:text-tp13-red transition-colors">
                  {EMAIL_SUPPORT}
                </a>
                <a href="tel:0261537228" className="hover:text-tp13-red transition-colors">
                  {PHONE_PRO}
                </a>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-tp13-border">
            <button
              onClick={handleClose}
              className="w-full px-6 py-2.5 text-tp13-muted hover:text-tp13-text transition-colors cursor-pointer"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
